/**
 * Routing helpers for Vault items that are mirrors of a Write/Present/Grid
 * document (services/docs), vs. plain uploaded files.
 */
import type { DriveItem } from "../api/types/drive";

/**
 * Returns the router path to navigate to for an already-linked item
 * (a Vault mirror of a docs.Document), or null if the item isn't linked.
 *
 * The owning module (write/present/grid) is derived from `mime_type`,
 * which `ensure_vault_mirror`'s create call sets to one of the
 * `application/vnd.weco.*` sentinel values — see services/docs's
 * `_TYPE_TO_MIMETYPE` (Task 4).
 */
export function linkedItemRoute(item: DriveItem): string | null {
  if (item.external_service !== "docs" || !item.external_document_id) return null;
  const base =
    item.mime_type === "application/vnd.weco.presentation"
      ? "/present"
      : item.mime_type === "application/vnd.weco.spreadsheet"
        ? "/grid"
        : "/write";
  return `${base}/${item.external_document_id}`;
}

/**
 * DOCX mime types Vault can hand off to Write's import bridge
 * (`/write/import-from-vault/:itemId`, Task 11).
 */
const DOCX_MIMES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

/**
 * PPTX mime types Vault can hand off to Present's import bridge
 * (`/present/import-from-vault/:itemId`, Task 12).
 */
const PPTX_MIMES = [
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.oasis.opendocument.presentation",
];

/**
 * XLSX mime types Vault can hand off to Grid's import bridge
 * (`/grid/import-from-vault/:itemId`, Task 13).
 */
const XLSX_MIMES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

/**
 * CSV mime types Vault can hand off to Grid's import bridge, same route as
 * XLSX (`/grid/import-from-vault/:itemId`) — the bridge itself picks the
 * right parser (ExcelJS vs. plain-text CSV) based on the item's mime type.
 */
const CSV_MIMES = ["text/csv"];

const DOCX_EXTS = ["docx", "doc"];
const PPTX_EXTS = ["pptx", "ppt", "odp"];
const XLSX_EXTS = ["xlsx", "xls"];
const CSV_EXTS = ["csv"];

/**
 * Lowercased file extension (no dot), or "" if the name has none. Matches on
 * the final `.`-separated segment only, so "report.pptx.pdf" resolves to
 * "pdf", not "pptx".
 */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot + 1).toLowerCase();
}

/**
 * Returns the router path to navigate to for an unlinked file whose
 * mime type (or, failing that, file extension) is importable into
 * Write/Present/Grid, or null otherwise.
 *
 * DOCX → Write's import bridge (Task 11). PPTX → Present's import bridge
 * (Task 12). XLSX/CSV → Grid's import bridge (Task 13).
 *
 * The backend derives `mime_type` server-side via content-sniffing with an
 * extension-based fallback (services/drive's `detect_mimetype`), so it's
 * already independent of the uploading OS/browser. `fileName` is checked
 * too as defense-in-depth for the rare case a stored mime type is missing
 * or generic (e.g. a pre-existing upload from before that normalization
 * existed) — extension matching is itself OS-agnostic since it's a plain
 * case-insensitive string comparison, not a platform mime-type lookup.
 */
export function importableMimeToBridgeRoute(
  mimeType: string | null,
  itemId: string,
  fileName?: string | null,
): string | null {
  const ext = fileName ? extensionOf(fileName) : "";
  if ((mimeType && DOCX_MIMES.includes(mimeType)) || DOCX_EXTS.includes(ext)) {
    return `/write/import-from-vault/${itemId}`;
  }
  if ((mimeType && PPTX_MIMES.includes(mimeType)) || PPTX_EXTS.includes(ext)) {
    return `/present/import-from-vault/${itemId}`;
  }
  if (
    (mimeType && (XLSX_MIMES.includes(mimeType) || CSV_MIMES.includes(mimeType))) ||
    XLSX_EXTS.includes(ext) ||
    CSV_EXTS.includes(ext)
  ) {
    return `/grid/import-from-vault/${itemId}`;
  }
  return null;
}
